import styled from 'styled-components';

const MONO_FONT =
  "'JetBrains Mono', 'Fira Code', ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace";

export const ChartContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
`;

export const SummaryStrip = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 8px;
`;

export const SummaryMetricBox = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 6px 10px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const SummaryMetricLabel = styled.span`
  font-size: 10.5px;
  color: #64748b;
  font-weight: 500;
`;

export const SummaryMetricValue = styled.span<{ $color?: string }>`
  font-size: 14px;
  font-weight: 700;
  color: ${(props) => props.$color || '#1e293b'};
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const SvgChartWrapper = styled.div`
  flex: 1;
  min-height: 190px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(180deg, #ffffff 0%, #f8fafc 100%);
  border: 1px solid #f1f5f9;
  border-radius: 8px;
  padding: 8px 6px 4px 6px;
`;

export const DonutLayout = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  gap: 12px;
`;

export const DonutSvgBox = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 2px 0;
`;

export const DonutLegendList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-height: 0;
  overflow-y: auto;
`;

export const DonutLegendRow = styled.div`
  display: grid;
  grid-template-columns: 12px 1fr 64px 54px;
  align-items: center;
  gap: 8px;
  padding: 5px 8px;
  border-radius: 6px;
  background: #f8fafc;
  border: 1px solid #f1f5f9;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    background: #eff6ff;
    border-color: #bfdbfe;
  }
`;

export const ColorDot = styled.span<{ $color: string }>`
  width: 9px;
  height: 9px;
  border-radius: 50%;
  background: ${(props) => props.$color};
  display: inline-block;
`;

export const LegendNameGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  min-width: 0;
`;

export const LegendName = styled.span`
  font-size: 11.5px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const LegendMiniBarTrack = styled.div`
  width: 100%;
  height: 4px;
  background: #e2e8f0;
  border-radius: 999px;
  overflow: hidden;
`;

export const LegendMiniBarFill = styled.div<{ $pct: number; $color: string }>`
  height: 100%;
  width: ${(props) => Math.max(2, Math.min(100, props.$pct))}%;
  background: ${(props) => props.$color};
  border-radius: 999px;
`;

export const LegendMonoCount = styled.span`
  text-align: right;
  font-size: 11.5px;
  font-weight: 600;
  color: #1e293b;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const LegendMonoShare = styled.span`
  text-align: right;
  font-size: 11px;
  font-weight: 600;
  color: #2563eb;
  background: #dbeafe;
  padding: 1px 5px;
  border-radius: 4px;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const SectionBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SectionSubTitle = styled.div`
  font-size: 11.5px;
  font-weight: 600;
  color: #475569;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

export const SegmentedProgressTrack = styled.div`
  display: flex;
  width: 100%;
  height: 10px;
  border-radius: 999px;
  overflow: hidden;
  background: #e2e8f0;
  gap: 2px;
`;

export const SegmentedProgressPiece = styled.div<{ $pct: number; $color: string }>`
  width: ${(props) => Math.max(3, props.$pct)}%;
  height: 100%;
  background: ${(props) => props.$color};
  transition: width 0.3s ease;
`;

export const GeomPillsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 6px;
`;

export const GeomPillCard = styled.div`
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 7px;
  padding: 6px 8px;
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

export const GeomPillHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 10.5px;
  font-weight: 600;
  color: #475569;
`;

export const GeomPillStats = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const GeomPillCount = styled.span`
  font-size: 13px;
  font-weight: 700;
  color: #1e293b;
`;

export const GeomPillPct = styled.span`
  font-size: 10px;
  color: #64748b;
`;

export const HourlyRowsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

export const HourlyRow = styled.div`
  display: grid;
  grid-template-columns: 58px 1fr 48px 44px;
  align-items: center;
  gap: 8px;
  font-size: 11px;
`;

export const HourlySlotLabel = styled.span`
  color: #475569;
  font-weight: 500;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const HourlyBarTrack = styled.div`
  width: 100%;
  height: 8px;
  background: #f1f5f9;
  border-radius: 999px;
  overflow: hidden;
`;

export const HourlyBarFill = styled.div<{ $pct: number; $isPeak?: boolean }>`
  height: 100%;
  width: ${(props) => Math.max(3, Math.min(100, props.$pct))}%;
  background: ${(props) =>
    props.$isPeak
      ? 'linear-gradient(90deg, #2563eb 0%, #3b82f6 100%)'
      : 'linear-gradient(90deg, #60a5fa 0%, #93c5fd 100%)'};
  border-radius: 999px;
  transition: width 0.3s ease;
`;

export const HourlyMonoCount = styled.span`
  text-align: right;
  font-weight: 600;
  color: #1e293b;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;

export const HourlyMonoPct = styled.span`
  text-align: right;
  font-size: 10.5px;
  color: #64748b;
  font-family: ${MONO_FONT};
  font-variant-numeric: tabular-nums;
`;
